import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { DbAppointmentResult } from "@/types/database";
import { SystemUpdateService } from '@/services/system-update.service';

export type AppointmentResultStatus = "scheduled" | "completed" | "cancelled" | "no_show" | "rescheduled";
export type AppointmentResultValue = "booked" | "not_interested" | "needs_more_time" | "wrong_number" | "voicemail" | "deferred" | null;

// Extended type to include resolved rep names from profile joins
export interface DbAppointmentResultWithProfile extends DbAppointmentResult {
  assigned_rep_name?: string;
}

export interface UpsertAppointmentResultPayload {
  appointmentId: string;          // unique key — use opportunity/lead id
  leadId?: string;
  scheduledAt: string;
  status: AppointmentResultStatus;
  result?: AppointmentResultValue;
  notes?: string;
  nextAction?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  createdBy: string;
  // Rich result data — persisted to metadata JSONB column
  pricingSnapshot?: {
    standardPrice: number;
    promoPrice: number;
    bestValuePrice: number;
    paymentMethod: string;
    productSummary: string;
  };
  noSaleReason?: string;
  noDemoReason?: string;
  rescheduleDatetime?: string;
  /** Canonical, persisted event time for commission attribution on sold outcomes */
  commissionEarnedAt?: string;
}

const APPOINTMENT_RESULTS_CACHE_TTL_MS = 20_000;
const APPOINTMENT_RESULT_SELECT_COLUMNS = [
  "id",
  "appointment_id",
  "lead_id",
  "quote_id",
  "scheduled_at",
  "status",
  "result",
  "notes",
  "duration_minutes",
  "next_action",
  "follow_up_task_id",
  "contact_name",
  "contact_phone",
  "contact_email",
  "created_by",
  "created_at",
  "updated_at",
  "organization_id",
  "metadata",
].join(", ");
const APPOINTMENT_RESULT_LEAD_SELECT_COLUMNS = [
  "id",
  "assigned_rep",
  "primary_name",
  "stage",
  "lead_source",
  "source_name",
  "lead_source_id",
].join(", ");
let appointmentResultsCache: { at: number; data: DbAppointmentResult[] | null; error: unknown } | null = null;
let appointmentResultsInFlight: Promise<{ data: DbAppointmentResult[] | null; error: unknown }> | null = null;

function invalidateAppointmentResultsCache() {
  appointmentResultsCache = null;
  appointmentResultsInFlight = null;
}

export const AppointmentResultService = {
  async getAll(options?: { forceRefresh?: boolean }): Promise<{ data: DbAppointmentResult[] | null; error: unknown }> {
    const forceRefresh = options?.forceRefresh === true;
    if (forceRefresh) {
      invalidateAppointmentResultsCache();
    }
    const now = Date.now();
    if (!forceRefresh && appointmentResultsCache && now - appointmentResultsCache.at < APPOINTMENT_RESULTS_CACHE_TTL_MS) {
      return { data: appointmentResultsCache.data, error: appointmentResultsCache.error };
    }
    if (!forceRefresh && appointmentResultsInFlight) {
      return await appointmentResultsInFlight;
    }

    const request = (async () => {
    const { data: results, error } = await supabase
      .from("appointment_results")
      .select(APPOINTMENT_RESULT_SELECT_COLUMNS)
      .order("scheduled_at", { ascending: false });

    if (error || !results || results.length === 0) {
      const payload = { data: results as DbAppointmentResult[] | null, error };
      appointmentResultsCache = { at: Date.now(), data: payload.data, error: payload.error };
      return payload;
    }

    const leadIds = [...new Set(
      results.map((r) => r.lead_id).filter((id): id is string => !!id),
    )];

    const leadsMap: Record<string, unknown> = {};
    if (leadIds.length > 0) {
      const { data: leads } = await supabase
        .from("leads")
        .select(APPOINTMENT_RESULT_LEAD_SELECT_COLUMNS)
        .in("id", leadIds);

      if (leads) {
        for (const lead of leads) {
          leadsMap[lead.id] = lead;
        }
      }
    }

    // Collect all unique assigned_rep IDs (profile UUIDs) from leads to resolve names
    const repIds = new Set<string>();
    for (const leadId of Object.keys(leadsMap)) {
      const lead = leadsMap[leadId] as Record<string, unknown> | undefined;
      const assignedRep = lead?.assigned_rep;
      if (typeof assignedRep === "string" && assignedRep) {
        repIds.add(assignedRep);
      }
    }

    // Resolve profile names for the rep IDs
    const repNameById = new Map<string, string>();
    if (repIds.size > 0) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, auth_user_id, full_name, first_name, last_name")
        .in("id", Array.from(repIds));

      if (profiles) {
        for (const p of profiles as Record<string, unknown>[]) {
          const first = String(p.first_name ?? "").trim();
          const last = String(p.last_name ?? "").trim();
          const fallback = [first, last].filter(Boolean).join(" ").trim();
          const name = String(p.full_name ?? "").trim() || fallback;
          const profileId = String(p.id ?? "").trim();
          const authUserId = String(p.auth_user_id ?? "").trim();
          if (profileId && name) repNameById.set(profileId, name);
          if (authUserId && name) repNameById.set(authUserId, name);
        }
      }
    }

// Enrich leads with resolved rep names
     for (const leadId of Object.keys(leadsMap)) {
       const lead = leadsMap[leadId] as Record<string, unknown>;
       const assignedRep = lead?.assigned_rep;
       if (typeof assignedRep === "string" && assignedRep) {
         const repName = repNameById.get(assignedRep);
         (leadsMap as Record<string, Record<string, unknown>>)[leadId] = {
           ...lead,
           // Only set assigned_rep_name if we have a valid non-UUID name
           ...(repName && !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(repName.trim())
             ? { assigned_rep_name: repName }
             : {}),
         };
       }
     }

    const enriched = results.map((r) => ({
      ...r,
      leads: r.lead_id ? (leadsMap[r.lead_id] ?? null) : null,
    })) as DbAppointmentResult[];

      const payload = { data: enriched, error: null };
      appointmentResultsCache = { at: Date.now(), data: payload.data, error: payload.error };
      return payload;
    })();
    appointmentResultsInFlight = request;
    try {
      return await request;
    } finally {
      appointmentResultsInFlight = null;
    }
  },

  async getByLeadId(leadId: string): Promise<DbAppointmentResult[]> {
    const { data, error } = await supabase
      .from("appointment_results")
      .select(APPOINTMENT_RESULT_SELECT_COLUMNS)
      .eq("lead_id", leadId)
      .order("scheduled_at", { ascending: true });

    if (error || !data) {
      console.warn("[AppointmentResultService.getByLeadId] error:", error);
      return [];
    }
    return data as DbAppointmentResult[];
  },

  async upsert(payload: UpsertAppointmentResultPayload) {
    let organizationId: string | null = null;
    try {
      organizationId = await getCurrentOrganizationId();
    } catch {
      // proceed without org — RLS will handle it
    }

    const shouldStampCommissionDate =
      payload.status === "completed" &&
      payload.result === "booked";
    const commissionEarnedAt =
      payload.commissionEarnedAt ??
      (shouldStampCommissionDate ? new Date().toISOString() : undefined);

    const metadata = (
      payload.pricingSnapshot ||
      payload.noSaleReason ||
      payload.noDemoReason ||
      payload.rescheduleDatetime ||
      commissionEarnedAt
    ) ? {
      pricingSnapshot: payload.pricingSnapshot,
      noSaleReason: payload.noSaleReason,
      noDemoReason: payload.noDemoReason,
      rescheduleDatetime: payload.rescheduleDatetime,
      commissionEarnedAt,
    } : null;

    const { data, error } = await supabase
      .from("appointment_results")
      .upsert(
        {
          appointment_id: payload.appointmentId,
          lead_id: payload.leadId ?? null,
          scheduled_at: payload.scheduledAt,
          status: payload.status,
          result: payload.result ?? null,
          notes: payload.notes ?? null,
          next_action: payload.nextAction ?? null,
          contact_name: payload.contactName ?? null,
          contact_phone: payload.contactPhone ?? null,
          contact_email: payload.contactEmail ?? null,
          created_by: payload.createdBy,
          organization_id: organizationId,
          metadata,
        },
        { onConflict: "appointment_id" },
      );

    if (error) console.warn("AppointmentResultService.upsert error:", error);
    if (!error) {
      invalidateAppointmentResultsCache();
      void SystemUpdateService.post({
        domain: 'pipeline',
        event:  'appointment_result_logged',
        body:   `System: Appointment Result Logged — ${payload.result ?? 'logged'}`,
      });
    }
    return { data, error };
  },
};
